import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Plus, MessageSquareText, Loader2 } from "lucide-react";
import ClientShell from "./ClientShell";
import { Button } from "../../components/ui/button";
import { useFeedbackIndex, useFeedbackRequests } from "@/lib/feedbackQueries";
import { useFileUploadContext } from "@/lib/fileQueries";
import FeedbackCard from "./feedback/FeedbackCard";
import RequestFeedbackDialog from "./feedback/RequestFeedbackDialog";
import FeatureOnboarding from "./onboarding/FeatureOnboarding";

const TABS = [
  { key: "all", label: "All" },
  { key: "sessions", label: "Sessions" },
  { key: "practice", label: "Practice" },
  { key: "presentations", label: "Presentations & Contexts" },
  { key: "files", label: "Files" },
  { key: "layers", label: "Layers" },
];
const FILTER_GROUPS = {
  sessions: ["session"], practice: ["assignment", "interaction_attempt", "rehearsal"],
  presentations: ["presentation_project", "section", "presence_context"], files: ["file_asset"], layers: ["layer"],
};

/**
 * Master v5 Prompt 12 — the coaching Feedback archive (never programme/
 * coach evaluation). Client filters: All, Sessions, Practice,
 * Presentations/Contexts, Files, Layers — applied to both published
 * Feedback and open/resolved FeedbackRequests together.
 */
export default function FeedbackPage() {
  const { workspaceId } = useParams();
  const [tab, setTab] = useState("all");
  const [requestOpen, setRequestOpen] = useState(false);
  const { data: uploadContext } = useFileUploadContext(workspaceId);
  const { data: feedbackData, isPending: feedbackPending } = useFeedbackIndex(workspaceId, tab);
  const { data: requestData, isPending: requestPending } = useFeedbackRequests(workspaceId);

  const isCoach = uploadContext?.workspace_role === "coach";
  const isTeamWorkspace = uploadContext?.access_type === "team";

  const requests = useMemo(() => {
    const items = requestData?.items || [];
    if (tab === "all") return items;
    const allowed = FILTER_GROUPS[tab] || [];
    return items.filter((i) => allowed.includes(i.evidence.target_type));
  }, [requestData, tab]);
  const feedbackItems = feedbackData?.items || [];
  const openRequests = requests.filter((r) => r.status === "open");
  const myOpenRequests = openRequests.filter((r) => r.is_mine);
  const myResolvedRequests = requests.filter((r) => r.status !== "open" && r.is_mine);
  const teamRequests = requests.filter((r) => !r.is_mine);

  return (
    <ClientShell>
      <div className="max-w-3xl" data-testid="feedback-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-serif text-2xl text-ink flex items-center gap-2"><MessageSquareText size={22} className="text-terracotta" /> Feedback</h1>
            <p className="text-ink-soft text-sm mt-1">Coaching feedback on your real work — never a programme review.</p>
          </div>
          {!isCoach && (
            <Button onClick={() => setRequestOpen(true)} data-testid="feedback-page-request-btn">
              <Plus size={15} className="mr-1.5" /> Request Feedback
            </Button>
          )}
        </div>

        <FeatureOnboarding
          workspaceId={workspaceId}
          featureKey="feedback"
          title="Welcome to Feedback"
          body={isCoach
            ? "Draft feedback stays private until you publish it. Once published it's read-only and evidence-linked — your client responds in its own linked reflection."
            : "Request feedback on a real Session, Practice attempt, File or Layer — pick exactly what you want reviewed and ask a specific question."}
        />

        <div className="flex flex-wrap gap-1.5 mb-6" data-testid="feedback-page-tabs">
          {TABS.map((t) => (
            <button
              key={t.key} onClick={() => setTab(t.key)}
              className={`text-xs font-medium px-3 py-1.5 rounded-full transition-colors ${tab === t.key ? "bg-terracotta text-white" : "bg-ink/5 text-ink-soft hover:bg-ink/10"}`}
              data-testid={`feedback-page-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {(feedbackPending || requestPending) ? (
          <div className="flex items-center gap-2 text-ink-soft" data-testid="feedback-page-loading"><Loader2 size={16} className="animate-spin" /> Loading feedback…</div>
        ) : (
          <div className="space-y-8">
            {isCoach && openRequests.length > 0 && (
              <section>
                <h2 className="text-sm font-semibold text-ink mb-2">Awaiting your feedback ({openRequests.length})</h2>
                <div className="space-y-3">
                  {openRequests.map((r) => <FeedbackCard key={r.frq_id} kind="request" item={r} />)}
                </div>
              </section>
            )}

            <section>
              <h2 className="text-sm font-semibold text-ink mb-2">Feedback</h2>
              {feedbackItems.length === 0 ? (
                <p className="text-ink-soft text-sm" data-testid="feedback-page-empty">No feedback yet.</p>
              ) : (
                <div className="space-y-3" data-testid="feedback-page-list">
                  {feedbackItems.map((f) => <FeedbackCard key={f.fbk_id} kind="feedback" item={f} />)}
                </div>
              )}
            </section>

            {!isCoach && myResolvedRequests.length > 0 && (
              <section>
                <h2 className="text-sm font-semibold text-ink mb-2">Your requests</h2>
                <div className="space-y-3">
                  {myResolvedRequests.map((r) => <FeedbackCard key={r.frq_id} kind="request" item={r} />)}
                </div>
              </section>
            )}
            {!isCoach && myOpenRequests.length > 0 && (
              <section>
                <h2 className="text-sm font-semibold text-ink mb-2">Open requests</h2>
                <div className="space-y-3">
                  {myOpenRequests.map((r) => <FeedbackCard key={r.frq_id} kind="request" item={r} />)}
                </div>
              </section>
            )}
            {!isCoach && teamRequests.length > 0 && (
              <section>
                <h2 className="text-sm font-semibold text-ink mb-2">Team requests</h2>
                <div className="space-y-3">
                  {teamRequests.map((r) => <FeedbackCard key={r.frq_id} kind="request" item={r} />)}
                </div>
              </section>
            )}
          </div>
        )}

        <RequestFeedbackDialog open={requestOpen} onOpenChange={setRequestOpen} workspaceId={workspaceId} isTeamWorkspace={isTeamWorkspace} />
      </div>
    </ClientShell>
  );
}
